import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import StatusChip from "../../BottlerForecast/Dashboard/StatusChip";
import {
  safeTimestamp,
  Timestamp,
} from "../../../Components/Common/DateFormatter";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import { Link } from "react-router-dom";
import DataTable from "../../../Components/Common/datatable/DataTable";
import Modal from "../../../Components/Common/Modal/Modal";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import { useSupport } from "../../../context/SupportContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FGtriggerRefresh } from "../../../redux/refresh";
import { useMemo } from "react";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";

function FGPastForecastTracker() {
  const GridRef = useRef(null);
  const modalGridRef = useRef(null);
  let { setfgPopupCombinationDetails, setfgCard2CombinationDetails } =
    useSupport();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [selectedRowData, setSelectedRowData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [modalRowData, setModalRowData] = useState([]);

  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [showSuccessOnly, setShowSuccessOnly] = useState(false);

  const finalTreeData = useSelector((state) => state.FGentity.FGfinalTreeData);
  const FGrefreshKey = useSelector((state) => state.refresh.FGrefreshKey);
  const [activeModalDefinition, setActiveModalDefinition] = useState([]);

  useEffect(() => {
    const loadPastForecastHistory = async () => {
      setLoading(true);
      try {
        const payload = {
          dashboard: true,
          combinationList: finalTreeData,
        };

        const response = await ApiService.post(
          "/v1/fg-inventory/fetch-customer-history-details",
          payload,
        );

        setFilteredData(response.data);
      } catch (error) {
        console.error("Error loading past forecast history", error);
      } finally {
        setLoading(false);
      }
    };

    if (finalTreeData?.length > 0) {
      loadPastForecastHistory();
    } else {
      setFilteredData([]);
      setLoading(false);
    }
  }, [finalTreeData, FGrefreshKey]);

  const columnDefinition = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        field: "supportCheckbox",
        headerName: "",
        sortable: false,
        width: 50,
        minWidth: 50,
        suppressSizeToFit: true,
      },
      {
        field: "bottler",
        headerName: "Bottler",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 140,
        minWidth: 140,
        cellRenderer: (params) => (
          <Tooltip title={params.data.bottler || ""} arrow>
            <div>{params.data.bottler}</div>
          </Tooltip>
        ),
      },
      {
        field: "bottlerOwnerCode",
        headerName: "Bottler Owner Code",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 230,
        minWidth: 230,
        valueGetter: (params) => {
          const { bottlerOwnerCode, bottlerOwnerDescription } = params.data;
          return `${bottlerOwnerCode || ""}-${bottlerOwnerDescription || ""}`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        field: "profitCenterCountry",
        headerName: "Customer Country",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 230,
        minWidth: 230,
        cellRenderer: (params) => (
          <Tooltip title={params.data.profitCenterCountry || ""} arrow>
            <div>{params.data.profitCenterCountry}</div>
          </Tooltip>
        ),
      },
      {
        field: "fiscalMonth",
        headerName: "Fiscal Period",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 180,
        minWidth: 180,
      },
      {
        field: "forecastMode",
        headerName: "Source",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 140,
        minWidth: 140,
      },
      {
        field: "templateType",
        headerName: "Template Type",
        sortable: true,
        unSortIcon: true,
        hide: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 210,
        minWidth: 210,
      },
      {
        field: "fileStatus",
        headerName: "Upload Status",
        sortable: true,
        unSortIcon: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 190,
        minWidth: 190,
        cellRenderer: (params) => <StatusChip status={params.value} />,
      },
      {
        field: "createdAt",
        headerName: "Upload Date Time(UTC)",
        sortable: true,
        sort: "desc",
        filterParams: {
          comparator: function (filterDate, cellValue) {
            if (!cellValue) return -1;

            const cellDate = new Date(cellValue);
            const cellDateOnly = new Date(
              cellDate.getFullYear(),
              cellDate.getMonth(),
              cellDate.getDate(),
            );

            return cellDateOnly.getTime() - filterDate.getTime();
          },
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
        },
        filter: "agDateColumnFilter",
        width: 260,
        minWidth: 260,
        valueFormatter: (params) => {
          return safeTimestamp(params.value);
        },
      },
      {
        field: "createdBy",
        headerName: "Upload By",
        sortable: true,
        unSortIcon: true,
        // hide: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        width: 190,
        minWidth: 190,
        cellRenderer: (params) => (
          <Tooltip title={params.data.createdBy || ""} arrow>
            <div>{params.data.createdBy}</div>
          </Tooltip>
        ),
      },
      {
        field: "noOfLineItems",
        headerName: "Upload Record Count",
        sortable: true,
        unSortIcon: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
        },
        width: 240,
        minWidth: 240,
      },
      {
        headerName: "View",
        sortable: false,
        resizable: false,
        width: 100,
        minWidth: 100,
        cellRenderer: (params) => (
          <div
            onClick={() => handleOpen(params.data)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              cursor: "pointer",
            }}
          >
            <VisibilitySharpIcon />
          </div>
        ),
      },
    ],
    [],
  );

  const standardModalDefinition = useMemo(
    () => [
      {
        headerName: "BPP Description",
        sortable: true,
        unSortIcon: true,
        width: 260,
        minWidth: 260,

        valueGetter: (params) => {
          const bppId = params.data?.bppId ?? "";
          const bppDesc = params.data?.bppDescription ?? "";

          if (!bppId && !bppDesc) return "";

          return `${bppId} - ${bppDesc}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        field: "fiscalPeriod",
        headerName: "Fiscal Period",
        sortable: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        unSortIcon: true,
        width: 180,
        minWidth: 180,
      },
      {
        field: "quantity",
        headerName: "Quantity",
        sortable: true,
        filterParams: {
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
        },
        filter: "agNumberColumnFilter",
        unSortIcon: true,
        width: 160,
        minWidth: 160,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        unSortIcon: true,
        width: 150,
        minWidth: 150,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        cellRenderer: (params) => <StatusChip status={params.value} />,
      },
      {
        field: "remarks",
        headerName: "Comments",
        sortable: true,
        unSortIcon: true,
        resizable: false,
        width: 330,
        minWidth: 330,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        cellRenderer: (params) => (
          <Tooltip title={params.value} arrow>
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "500px",
              }}
            >
              {params.value}
            </div>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const customModalDefinition = useMemo(
    () => [
      {
        headerName: "SKU Description",
        sortable: true,
        unSortIcon: true,
        width: 260,
        minWidth: 260,

        valueGetter: (params) => {
          const bppId = params.data?.sku ?? "";
          const bppDesc = params.data?.customerSkuDescription ?? "";

          if (!bppId && !bppDesc) return "";

          return `${bppId} - ${bppDesc}`;
        },

        cellRenderer: (params) => {
          if (!params.value) return null;

          return (
            <Tooltip title={params.value} arrow>
              <div>{params.value}</div>
            </Tooltip>
          );
        },

        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            { filter: "agTextColumnFilter" },
            { filter: "agSetColumnFilter" },
          ],
        },
      },
      {
        field: "fiscalPeriod",
        headerName: "Fiscal Period",
        sortable: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        unSortIcon: true,
        width: 180,
        minWidth: 180,
      },
      {
        field: "quantity",
        headerName: "Quantity",
        sortable: true,
        filterParams: {
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
        },
        filter: "agNumberColumnFilter",
        unSortIcon: true,
        width: 160,
        minWidth: 160,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        unSortIcon: true,
        width: 150,
        minWidth: 150,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        cellRenderer: (params) => <StatusChip status={params.value} />,
      },
      {
        field: "remarks",
        headerName: "Comments",
        sortable: true,
        unSortIcon: true,
        resizable: false,
        width: 330,
        minWidth: 330,
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agTextColumnFilter",
            },
            {
              filter: "agSetColumnFilter",
            },
          ],
        },
        cellRenderer: (params) => (
          <Tooltip title={params.value} arrow>
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "500px",
              }}
            >
              {params.value}
            </div>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const handleSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const combinationDetails = selectedRows.map(
      ({ bottler, profitCenterCountry, bottlerOwnerCode }) => ({
        bottler,
        profitCenterCountry,
        bottlerOwnerCode,
      }),
    );

    setfgCard2CombinationDetails(combinationDetails);
  };

  const handleOpen = async (rowData) => {
    try {
      setModalLoading(true);
      setShowFailedOnly(false);
      setShowSuccessOnly(false);

      const isCustomTemplate = rowData?.templateType === "CUSTOM_TEMPLATE";

      console.log(isCustomTemplate, "isCustomTemplate");
      setActiveModalDefinition(
        isCustomTemplate ? customModalDefinition : standardModalDefinition,
      );

      const countryCode = rowData.profitCenterCountry?.split("-")[0];

      const fileResponse = await ApiService.get(
        `/v1/fg-inventory/get-fg-inventory-line-item-records?submissionId=${
          rowData.submissionId
        }&bottlerOwnerCode=${rowData.bottlerOwnerCode}&countryCode=${
          countryCode
        }`,
      );
      setModalRowData(fileResponse.data);
      setSelectedRowData(rowData);
      setfgPopupCombinationDetails([
        {
          bottler: rowData.bottler,
          profitCenterCountry: rowData.profitCenterCountry,
          bottlerOwnerCode: rowData.bottlerOwnerCode,
        },
      ]);
      setOpen(true);
    } catch (error) {
      console.error("Error fetching file", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRowData(null);
  };

  const handleFialureChange = (event) => {
    setShowFailedOnly(event.target.checked);
    if (event.target.checked) {
      setShowSuccessOnly(false);
    }
  };

  const handleSuccessChange = (event) => {
    setShowSuccessOnly(event.target.checked);
    if (event.target.checked) {
      setShowFailedOnly(false);
    }
  };

  const failedData = modalRowData.filter((row) => row.status === "ERROR");
  const successData = modalRowData.filter((row) => row.status === "SUCCESS");
  const dataToShow = showFailedOnly
    ? failedData
    : showSuccessOnly
      ? successData
      : modalRowData;

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const paginationModal = true;
  const paginationModalPageSize = 10;
  const paginationModalPageSizeSelector = [10, 50, 100, 500, 1000];

  const onGridReady = (params) => {
    params.api.selectAll();
  };

  const onModalGridExport = async () => {
    if (!selectedRowData) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "No row selected for export!",
      });
      return;
    }

    setExportLoading(true);
    const country = selectedRowData.profitCenterCountry;
    const customerCountry = country ? country.substring(0, 2) : undefined;
    const params = {
      status: showSuccessOnly ? "SUCCESS" : showFailedOnly ? "ERROR" : "BOTH",
      customerCountry,
      bottlerOwnerCode: selectedRowData.bottlerOwnerCode,
      submissionId: selectedRowData?.submissionId,
    };

    try {
      const response = await ApiService.downloadFile(
        "/v1/fg-inventory/download-fg-inventory-data",
        null,
        null,
        params,
      );

      if (response.status === 200 && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");
        const filename = `FG_Inventory_report_${selectedRowData?.fiscalMonth}_${timestamp}1.xlsx`;

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error during export:", error);
      MessagePopup({
        icon: "error",
        title: "Error!",
        message:
          error.response?.status === 404
            ? "Requested resource not found."
            : "An error occurred while exporting data.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className="container">
        <Grid xs={12}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={8} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom="true">
                Previous FG Inventory Uploads
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Grid>

            <Grid
              item
              xs={4}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Typography variant="subtitle1" align="right">
                <Typography variant="subtitle1" align="right">
                  <Link
                    to="/fg-history"
                    style={{ color: "grey", textDecoration: "underline" }}
                  >
                    View All History
                  </Link>
                </Typography>
              </Typography>
              <Tooltip title="Refresh" arrow placement="top">
                <RefreshIcon
                  sx={{
                    marginLeft: "10px",
                    cursor: "pointer",
                    color: "grey",
                    "&:hover": {
                      color: "gray",
                    },
                  }}
                  onClick={() => {
                    dispatch(FGtriggerRefresh());
                  }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={280}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={GridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              tableHeight={"50vh"}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              onSelectionChanged={handleSelectionChanged}
            />
          )}

          <Modal
            open={open}
            handleClose={handleClose}
            maxWidth={"100%"}
            height={450}
            title="Record Details"
            content={
              modalLoading ? (
                <Skeleton
                  variant="rectangular"
                  height={400}
                  animation="pulse"
                  style={{ marginBottom: "16px", borderRadius: "8px" }}
                />
              ) : (
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      maxWidth: "1100px",
                      marginBottom: "5px",
                    }}
                  >
                    <div style={{ flex: "1 1 25%" }}>
                      <TextField
                        id="ParentBottler"
                        label="Parent Bottler"
                        value={selectedRowData?.bottler || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    <div style={{ flex: "1 1 25%" }}>
                      <TextField
                        id="forecast-partner"
                        label="Bottler Owner Code"
                        value={
                          selectedRowData
                            ? `${selectedRowData.bottlerOwnerCode || ""}${
                                selectedRowData.bottlerOwnerDescription
                                  ? ` - ${selectedRowData.bottlerOwnerDescription}`
                                  : ""
                              }`
                            : ""
                        }
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    <div style={{ flex: "1 1 25%" }}>
                      <TextField
                        id="profiCenterCountry"
                        label="Customer Country"
                        value={selectedRowData?.profitCenterCountry || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    <div style={{ flex: "1 1 40%" }}>
                      <TextField
                        id="fiscalPeriod"
                        label="Fiscal Period"
                        value={selectedRowData?.fiscalMonth || ""}
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>

                    <div style={{ flex: "1 1 40%" }}>
                      <TextField
                        id="uuploadDateTime"
                        label="Upload Date Time (UTC)"
                        value={
                          selectedRowData?.createdAt
                            ? safeTimestamp(selectedRowData.createdAt)
                            : ""
                        }
                        InputProps={{
                          style: {
                            fontSize: "13px",
                            height: "30px",
                            cursor: "default",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    </div>
                  </div>

                  <div className="filter-container">
                    <div className="span-container">
                      <span className="button-container">Filters</span>
                      <div className="filter-button">
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showFailedOnly}
                                onChange={handleFialureChange}
                              />
                            }
                            label="Failed rows"
                            style={{ marginRight: 0 }}
                            labelPlacement="start"
                            componentsProps={{
                              typography: {
                                style: {
                                  marginRight: "auto",
                                  fontSize: "13px",
                                },
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                      <div className="filter-button">
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showSuccessOnly}
                                onChange={handleSuccessChange}
                              />
                            }
                            label="Successful rows"
                            style={{ marginRight: 0 }}
                            labelPlacement="start"
                            componentsProps={{
                              typography: {
                                style: {
                                  marginRight: "auto",
                                  fontSize: "13px",
                                },
                              },
                            }}
                          />
                        </FormGroup>
                      </div>
                    </div>

                    <button
                      className="download-button"
                      onClick={onModalGridExport}
                      disabled={exportLoading}
                    >
                      {exportLoading ? "Downloading..." : "Download"}{" "}
                      <CloudDownloadOutlinedIcon className="download-icon" />
                    </button>
                  </div>
                  <div className="ag-theme-alpine" style={{ width: "100%" }}>
                    <DataTable
                      ref={modalGridRef}
                      columns={activeModalDefinition}
                      rows={dataToShow}
                      pagination={paginationModal}
                      paginationPageSize={paginationModalPageSize}
                      paginationPageSizeSelector={
                        paginationModalPageSizeSelector
                      }
                      tableHeight={"50vh"}
                      tablewidth={1100}
                      suppressMovableColumns={true}
                      suppressPaginationPanel={false}
                      suppressRowClickSelection={true}
                      suppressCsvExport={false}
                      onGridReady={onGridReady}
                    />
                  </div>
                </div>
              )
            }
          />
        </Grid>
      </div>
    </>
  );
}

export default FGPastForecastTracker;
